"use client";

import { useCallback, useMemo, useState } from "react";
import {
  Box,
  chakra,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Flex,
  Spinner,
} from "@chakra-ui/react";
import { isValidResumeFile } from "../utils/helpers";
import { Emoji } from "@/_components/ui/Emoji";
import type { EmojiKey } from "@/_components/ui/Emoji/emojiList";
import { SelfAds2Option } from "@/_components/ui/SelfAds2Option";
import { useAuthStore } from "@/_stores/useAuthStore";
import { authService } from "@/_services/userServices/auth";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";

interface UploadScreenProps {
  onAnalyze: (file: File) => void;
  isLoading: boolean;
}

const FEATURE_KEYS = [
  { icon: "page-with-curl" as EmojiKey, key: "atsScore" },
  { icon: "light-bulb" as EmojiKey, key: "aiInsights" },
  { icon: "label" as EmojiKey, key: "skillRadar" },
  { icon: "rocket" as EmojiKey, key: "actionPlan" },
] as const;

export const UploadScreen = ({ onAnalyze, isLoading }: UploadScreenProps) => {
  const t = useTranslations("resume_benchmark");
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { token } = useAuthStore();
  const locale = useLocale();
  const router = useRouter();

  const features = useMemo(
    () =>
      FEATURE_KEYS.map((f) => ({
        icon: f.icon,
        label: t(`upload.features.${f.key}.label`),
        desc: t(`upload.features.${f.key}.desc`),
      })),
    [t],
  );

  const formatSize = useCallback(
    (bytes: number) => {
      if (bytes < 1024) return t("fileSize.bytes", { size: bytes });
      if (bytes < 1024 * 1024)
        return t("fileSize.kb", { size: (bytes / 1024).toFixed(1) });
      return t("fileSize.mb", {
        size: (bytes / (1024 * 1024)).toFixed(1),
      });
    },
    [t],
  );

  const dropZoneBg = useMemo(() => {
    if (isDragging) return "rgba(99,102,241,0.08)";
    if (file) return "rgba(34,197,94,0.06)";
    return "whiteAlpha.50";
  }, [isDragging, file]);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback(
    (f: File) => {
      setError(null);
      if (!isValidResumeFile(f)) {
        setError(t("upload.errors.invalidType"));
        return;
      }
      if (f.size > 10 * 1024 * 1024) {
        setError(t("upload.errors.tooLarge"));
        return;
      }
      setFile(f);
    },
    [t],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) handleFile(dropped);
    },
    [handleFile],
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (selected) handleFile(selected);
  };

  const handleEmployerClick = () => {
    if (token) {
      authService.logout("/auth/login");
    } else {
      router.push("/auth/login");
    }
  };
  const handleCandidateClick = () => {
    router.push(`/${locale}/resume-benchmark/guide`);
  };

  return (
    <Box
      minH="100vh"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      px={{ base: 4, md: 8 }}
      py={12}
    >
      <VStack gap={12} maxW="900px" mx="auto" w="full">
        {/* Hero */}
        <VStack gap={4} textAlign="center">
          <Badge
            px={4}
            py={1.5}
            rounded="full"
            fontSize="xs"
            fontWeight="bold"
            bg="blackAlpha.800"
            color="primary.400"
            letterSpacing="widest"
            textTransform="uppercase"
          >
            {t("upload.badge")}
          </Badge>
          <Text
            fontSize={{ base: "3xl", md: "5xl" }}
            fontWeight="black"
            lineHeight="1.1"
            bgGradient="to-r"
            gradientFrom="white"
            gradientTo="whiteAlpha.600"
            bgClip="text"
          >
            {t("upload.headlineLine1")}
            <br />
            {t("upload.headlineLine2")}
          </Text>
          <Text
            fontSize={{ base: "sm", md: "md" }}
            color="whiteAlpha.600"
            maxW="520px"
          >
            {t("upload.subhead")}
          </Text>
        </VStack>

        {/* Features pills */}
        <Flex gap={3} flexWrap="wrap" justify="center">
          {features.map((f) => (
            <HStack
              key={f.label}
              px={4}
              py={2}
              rounded="full"
              gap={2}
              bg="whiteAlpha.50"
              border="1px solid"
              borderColor="whiteAlpha.100"
              backdropFilter="blur(8px)"
            >
              <Emoji emoji={f.icon} size={5} />
              <VStack gap={0} align="flex-start">
                <Text
                  fontSize="xs"
                  fontWeight="bold"
                  color="neutral.100"
                  lineHeight="1"
                >
                  {f.label}
                </Text>
                <Text
                  fontSize="2xs"
                  color="neutral.400"
                  lineHeight="1.3"
                  mt={1}
                >
                  {f.desc}
                </Text>
              </VStack>
            </HStack>
          ))}
        </Flex>

        {/* Drop zone */}
        <Box w="full">
          <chakra.label
            htmlFor="resume-upload"
            display="block"
            cursor="pointer"
            rounded="3xl"
            border="2px dashed"
            borderColor={
              isDragging ? "indigo.400" : file ? "green.400" : "whiteAlpha.200"
            }
            bg={dropZoneBg}
            transition="all 0.25s"
            onDragOver={(e: React.DragEvent) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={handleDrop}
            p={{ base: 8, md: 12 }}
            _hover={{ borderColor: "indigo.400", bg: "rgba(99,102,241,0.06)" }}
          >
            <VStack gap={4} textAlign="center">
              {file ? (
                <>
                  <Emoji emoji="page-with-curl" size={12} />
                  <VStack gap={1}>
                    <Text fontSize="sm" fontWeight="bold" color="green.300">
                      {file.name}
                    </Text>
                    <Text fontSize="xs" color="neutral.400">
                      {formatSize(file.size)}
                    </Text>
                  </VStack>
                  <Text fontSize="xs" color="neutral.400">
                    {t("upload.replace")}
                  </Text>
                </>
              ) : (
                <>
                  <Box
                    w={16}
                    h={16}
                    rounded="2xl"
                    bg="blackAlpha.400"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    fontSize="2xl"
                  >
                    <Emoji emoji="cloud" size={12} />
                  </Box>
                  <VStack gap={1}>
                    <Text fontSize="sm" fontWeight="bold" color="neutral.100">
                      {t("upload.dropHint")}{" "}
                      <Text as="span" color="indigo.300">
                        {t("upload.browse")}
                      </Text>
                    </Text>
                    <Text fontSize="xs" color="neutral.400">
                      {t("upload.formats")}
                    </Text>
                  </VStack>
                </>
              )}
            </VStack>
            <input
              id="resume-upload"
              type="file"
              accept=".pdf,.doc,.docx"
              style={{ display: "none" }}
              onChange={handleInputChange}
            />
          </chakra.label>

          {error && (
            <Text fontSize="xs" color="primary.400" mt={2} textAlign="center">
              {error}
            </Text>
          )}
        </Box>

        {/* CTA */}
        {file && (
          <VStack gap={3} w="full" maxW="400px">
            <Button
              w="full"
              size="lg"
              borderRadius="xl"
              bg="indigo.500"
              color="white"
              fontWeight="bold"
              fontSize="sm"
              _hover={{
                bg: "indigo.400",
                transform: "translateY(-1px)",
                boxShadow: "0 8px 32px rgba(99,102,241,0.4)",
              }}
              transition="all 0.2s"
              onClick={() => onAnalyze(file)}
              disabled={isLoading}
            >
              {isLoading ? (
                <HStack gap={2}>
                  <Spinner size="sm" />
                  <Text>{t("upload.ctaLoading")}</Text>
                </HStack>
              ) : (
                t("upload.cta")
              )}
            </Button>
            <HStack gap={2}>
              <Text fontSize="2xs" color="neutral.400">
                {t("upload.privacy")}
              </Text>
            </HStack>
          </VStack>
        )}
        <Box w="full" mt={10}>
          <SelfAds2Option
            onEmployerClick={handleEmployerClick}
            onCandidateClick={handleCandidateClick}
          />
        </Box>

        {/* Trust badges */}
        <HStack gap={6} flexWrap="wrap" justify="center">
          {[
            t("upload.trust.privacy"),
            t("upload.trust.speed"),
            t("upload.trust.users"),
          ].map((label) => (
            <Text key={label} fontSize="sm" color="neutral.400">
              {label}
            </Text>
          ))}
        </HStack>
      </VStack>
    </Box>
  );
};
